import axios from 'axios'

const API_URL =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_API_URL_DEV
    : process.env.REACT_APP_API_URL

export interface ReportFilters {
  speId?: string
  projetoId?: string
  dataInicio?: string
  dataFim?: string
  mesReferencia?: string
  sourceFilter?: 'uau' | 'manual' | 'all'
  emailOverride?: string
  // Bypass the daily calculated_total cache and refetch every parcel from UAU.
  // Slower (~5min under good UAU conditions) but always fresh. Use when the
  // client suspects the DB values are stale (e.g. UAU came back mid-day).
  forceUauRefresh?: boolean
}

export type ReportType =
  | 'estoque'
  | 'contratos-ativos'
  | 'inadimplentes'
  | 'inadimplencia-parcelas'
  | 'recebimento'
  | 'fluxo-futuro'
  | 'resumo'
  | 'completo'

const getReportFileName = (reportType: ReportType): string => {
  const date = new Date().toISOString().split('T')[0]
  const nameMap: Record<ReportType, string> = {
    estoque: `estoque-${date}.xlsx`,
    'contratos-ativos': `contratos-ativos-${date}.xlsx`,
    inadimplentes: `inadimplentes-${date}.xlsx`,
    'inadimplencia-parcelas': `inadimplencia-parcelas-${date}.xlsx`,
    recebimento: `recebimento-${date}.xlsx`,
    'fluxo-futuro': `fluxo-futuro-${date}.xlsx`,
    resumo: `resumo-${date}.xlsx`,
    completo: `relatorio-completo-${date}.xlsx`
  }
  return nameMap[reportType]
}

export async function downloadReportExcel(
  token: string,
  userContextEncoded: string | undefined,
  reportType: ReportType,
  filters?: ReportFilters
): Promise<void> {
  const endpoint =
    reportType === 'completo'
      ? `${API_URL}/reports/client/completo/excel`
      : `${API_URL}/reports/client/${reportType}/excel`

  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  const response = await axios.get(endpoint, {
    headers,
    params: filters,
    responseType: 'blob'
  })

  // Create download link
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = getReportFileName(reportType)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export async function downloadRecebimentoReport(
  token: string,
  userContextEncoded: string | undefined,
  filters?: ReportFilters
): Promise<void> {
  return downloadReportExcel(token, userContextEncoded, 'recebimento', filters)
}

export async function downloadRelatorioCompleto(
  token: string,
  userContextEncoded: string | undefined,
  filters?: ReportFilters
): Promise<{ message: string; jobId: string }> {
  const endpoint = `${API_URL}/reports/client/completo/excel`

  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  const response = await axios.get(endpoint, {
    headers,
    params: filters
  })

  return response.data
}

export interface SpeOption {
  id: string
  business_name: string
}

export async function fetchUserSpes(
  token: string,
  userContextEncoded: string | undefined
): Promise<SpeOption[]> {
  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  const response = await axios.get(`${API_URL}/spes?scope=minimal`, { headers })
  return response.data
}

export function getUserEmailFromToken(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.email || ''
  } catch {
    return ''
  }
}

export async function checkReportStatus(
  token: string,
  userContextEncoded: string | undefined
): Promise<{ hasActiveJob: boolean }> {
  const endpoint = `${API_URL}/reports/client/completo/status`

  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  const response = await axios.get(endpoint, { headers })
  return response.data
}
